import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import ProductReviews from '../../components/features/ProductReviews';
import { useCurrency } from '../../context/CurrencyContext';
import { useReviews } from '../../context/ReviewContext';
import { ArrowLeft, ShoppingBag, Share2, Users } from 'lucide-react';
import productsService from '../../services/productsService';
import ProductImageSlider from '../../components/features/ProductImageSlider';
import logger from '../../utils/logging';
import analyticsService from '../../services/analyticsService';
import PLACEHOLDER_IMAGE from '../../utils/placeholderImage';
import { useToast } from '../../context/ToastContext';

/** De-dupe and put catalog primary first when it is not already in the gallery array */
function mergePrimaryIntoImageList(primaryImage, images) {
  const out = [];
  const seen = new Set();
  const add = (u) => {
    if (!u || seen.has(u)) return;
    seen.add(u);
    out.push(u);
  };
  if (primaryImage) add(primaryImage);
  if (Array.isArray(images)) images.forEach(add);
  return out;
}

/** Catalog gallery in the middle slider is capped for performance and layout */
const MAX_FULL_GALLERY_SLIDES = 13;

const THREE_DAYS_MS = 72 * 60 * 60 * 1000;

function formatCountdownParts(ms) {
  if (ms <= 0) return null;
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return { d, h, m, sec };
}

const Product = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { showToast } = useToast();
  const { user } = useAuth();
  const { formatPrice } = useCurrency();
  const { getProductReviews } = useReviews();
  const [product, setProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [artistMoreProducts, setArtistMoreProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setSaleClockTick] = useState(0);
  const [validImages, setValidImages] = useState([]);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxCaption, setLightboxCaption] = useState('');
  const [sharing, setSharing] = useState(false);

  const openLightbox = (src, caption = '') => {
    setLightboxImage(src);
    setLightboxCaption(caption);
  };

  const closeLightbox = () => {
    setLightboxImage(null);
    setLightboxCaption('');
  };

  // Helper function to check if an image exists
  const checkImageExists = async (imagePath) => {
    try {
      const response = await fetch(imagePath, { method: 'HEAD' });
      return response.ok;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const loadProduct = async () => {
      try {
        logger.logComponentMount('Product', { slug });

        if (!slug) {
          setLoading(false);
          return;
        }

        // Try to fetch product directly by ID first (if slug is a UUID)
        let foundProduct = null;
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slug);

        if (isUUID) {
          foundProduct = await productsService.getProduct(slug);
        }

        if (!foundProduct) {
          const allProducts = await productsService.getProducts();
          foundProduct = allProducts.find(p => {
            const productSlug = p.slug || p.name.toLowerCase().replace(/\s+/g, '-');
            return productSlug === slug || p.id === slug;
          });

          if (foundProduct) {
            const fullProduct = await productsService.getProduct(foundProduct.id);
            if (fullProduct) {
              setProduct(fullProduct);
              logger.logProductView(fullProduct.id, fullProduct.name, { category: fullProduct.category, price: fullProduct.price });
              analyticsService.trackProductView(fullProduct);
            } else {
              setProduct(foundProduct);
              logger.logProductView(foundProduct.id, foundProduct.name, { category: foundProduct.category, price: foundProduct.price });
              analyticsService.trackProductView(foundProduct);
            }
          }
        } else {
          setProduct(foundProduct);
          logger.logProductView(foundProduct.id, foundProduct.name, { category: foundProduct.category, price: foundProduct.price });
          analyticsService.trackProductView(foundProduct);
        }

        if (!foundProduct) {
          setLoading(false);
          return;
        }
      } catch (error) {
        logger.logError(error, 'Product component');
        setLoading(false);
        return;
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [slug, navigate]);

  // Auto-select first variant when there's only one
  useEffect(() => {
    if (product?.variants?.length === 1 && selectedVariant === null) {
      setSelectedVariant(0);
    }
  }, [product?.variants, selectedVariant]);

  // Load related products when product is set (product?.id and product?.category are sufficient deps)
  useEffect(() => {
    if (!product) return;
    let cancelled = false;
    const category = product.category;
    const productId = product.id;
    productsService.getProducts()
      .then((all) => {
        if (cancelled) return;
        const related = all
          .filter((p) => p.id !== productId && p.isActive !== false)
          .filter((p) => !category || p.category === category)
          .slice(0, 4);
        setRelatedProducts(related.length ? related : all.filter((p) => p.id !== productId).slice(0, 4));
        const artistName = product?.artist?.trim();
        if (artistName) {
          const low = artistName.toLowerCase();
          const byArtist = all
            .filter((p) => p.id !== productId && p.isActive !== false && p.artist && p.artist.trim().toLowerCase() === low)
            .slice(0, 4);
          setArtistMoreProducts(byArtist);
        } else {
          setArtistMoreProducts([]);
        }
      })
      .catch(() => {
        setRelatedProducts([]);
        setArtistMoreProducts([]);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- id, category, artist sufficient for related + artist rails
  }, [product?.id, product?.category, product?.artist]);

  // Sale-aware price: use salePrice when on sale, else base price
  const getDisplayPrice = () => {
    if (!product) {
      return { price: 0, original: 0, onSale: false };
    }
    if (product.variants && product.variants.length > 0 && selectedVariant !== null) {
      const v = product.variants[selectedVariant];
      const vsp = product.variantSalePrices?.[v?.id];
      if (vsp?.saleInfo) return { price: vsp.salePrice, original: vsp.originalPrice, onSale: true };
      const p = typeof v?.price === 'string' ? parseFloat(String(v?.price || 0).replace(/[^0-9.]/g, '')) : parseFloat(v?.price || 0);
      return { price: p, original: p, onSale: false };
    }
    if (product.variants && product.variants.length > 0) {
      const prices = product.variants.map((v) => {
        const vsp = product.variantSalePrices?.[v.id];
        if (vsp?.saleInfo) return vsp.salePrice;
        return typeof v.price === 'string' ? parseFloat(String(v.price || 0).replace(/[^0-9.]/g, '')) : parseFloat(v.price || 0);
      });
      const minP = Math.min(...prices);
      const maxP = Math.max(...prices);
      return { price: minP, priceRange: [minP, maxP], original: null, onSale: prices.some((_, i) => product.variantSalePrices?.[product.variants[i]?.id]?.saleInfo) };
    }
    if (product.isOnSale && product.salePrice != null) {
      return { price: product.salePrice, original: product.originalPrice ?? product.price, onSale: true };
    }
    const p = parseFloat(product.price || 0);
    return { price: p, original: p, onSale: false };
  };

  const getEffectivePriceForCart = (variantOrProduct) => {
    if (!product) return 0;
    if (variantOrProduct?.price != null && product.variantSalePrices?.[variantOrProduct?.id]?.saleInfo) {
      return product.variantSalePrices[variantOrProduct.id].salePrice;
    }
    if (product.isOnSale && product.salePrice != null && !variantOrProduct) return product.salePrice;
    const raw = variantOrProduct?.price ?? product.price;
    return typeof raw === 'string' ? parseFloat(String(raw).replace(/[^0-9.]/g, '')) : parseFloat(raw || 0);
  };

  const displayPrice = getDisplayPrice();
  const saleInfo =
    product &&
    product.variants?.[selectedVariant] != null &&
    product.variantSalePrices?.[product.variants[selectedVariant].id]?.saleInfo
      ? product.variantSalePrices[product.variants[selectedVariant].id].saleInfo
      : product?.saleInfo ?? null;

  const handleShare = async () => {
    setSharing(true);
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const text = product ? `Check out ${product.name} on Rainbow Sails!` : 'Check out Rainbow Sails!';
    try {
      if (navigator.share) {
        await navigator.share({ title: product?.name || 'Rainbow Sails', text, url });
        showToast('Shared with friends!', 'success');
      } else {
        await navigator.clipboard.writeText(url);
        showToast('Link copied! Share with your friends.', 'success');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        await navigator.clipboard.writeText(url).catch(() => {});
        showToast('Link copied! Share with your friends.', 'success');
      }
    } finally {
      setSharing(false);
    }
  };

  // Validate images when product changes
  useEffect(() => {
    const validateImages = async () => {
      if (!product) return;

      const imagesToCheck = [];

      // For Mango products, use images array directly (already full URLs)
      if (product.images && Array.isArray(product.images) && product.images.length > 0) {
        product.images.forEach((imageUrl, index) => {
          imagesToCheck.push({
            src: imageUrl,
            label: index === 0 ? 'Primary Image' : `Image ${index + 1}`,
            type: index === 0 ? 'primary' : 'gallery',
            alt: `${product.name} - ${index === 0 ? 'Primary' : `Image ${index + 1}`}`,
            localPath: imageUrl // Already full URL from productsService
          });
        });
      }
      
      // Only check folder-based images if folder exists (legacy products)
      if (!product.folder) {
        setValidImages(imagesToCheck);
        return;
      }

      // Legacy folder-based system (for old products)
      const mainCustomImagePath = product.folder ? `/products/${product.folder}/custom-0.jpg` : null;
      if (mainCustomImagePath) {
        imagesToCheck.push({
          src: 'custom-0.jpg',
          label: 'Main Design',
          type: 'main-custom',
          alt: `${product.name} - Main Design`,
          localPath: mainCustomImagePath
        });
      }
      
      // Add custom images
      if (product.customImages && product.customImages.length > 0) {
        product.customImages.forEach((imageName, index) => {
          imagesToCheck.push({
            src: imageName,
            label: `Design ${index + 1}`,
            type: 'custom',
            alt: `${product.name} - Custom ${index + 1}`,
            localPath: `/products/${product.folder}/${imageName}`
          });
        });
      }
      
      // Add variant images using correct naming pattern (variant-0, variant-1, etc.)
      if (product.variants && product.variants.length > 0) {
        product.variants.forEach((variant, index) => {
          // Add preview image for each variant (all preview images)
          const previewImageName = `variant-${index}-preview-1.png`;
          imagesToCheck.push({
            src: previewImageName,
            label: `${variant.size || variant.name || `Variant ${index + 1}`} Preview`,
            type: 'variant-preview',
            alt: `${product.name} - ${variant.size || variant.name} Preview`,
            localPath: `/products/${product.folder}/${previewImageName}`
          });
        });
        
        // Add ONLY the first variant's default image (not all variants)
        if (product.variants.length > 0) {
          const firstVariantDefaultImageName = `variant-0-default-0.png`;
          imagesToCheck.push({
            src: firstVariantDefaultImageName,
            label: `${product.variants[0].size || product.variants[0].name || 'Variant 1'} Default`,
            type: 'variant-default',
            alt: `${product.name} - ${product.variants[0].size || product.variants[0].name} Default`,
            localPath: `/products/${product.folder}/${firstVariantDefaultImageName}`
          });
        }
      }
      
      // Add any additional gallery images if they exist
      if (product.galleryImages && Array.isArray(product.galleryImages)) {
        product.galleryImages.forEach((imageItem, index) => {
          // Handle both string filenames and image objects
          const imageName = typeof imageItem === 'string' ? imageItem : (imageItem.filename || imageItem.name || imageItem.url);
          if (imageName) {
            imagesToCheck.push({
              src: imageName,
              label: `Gallery ${index + 1}`,
              type: 'gallery',
              alt: `${product.name} - Gallery ${index + 1}`,
              localPath: `/products/${product.folder}/${imageName}`
            });
          }
        });
      }
      
      // Check which images actually exist
      const validImagesList = [];
      for (const image of imagesToCheck) {
        const exists = await checkImageExists(image.localPath);
        if (exists) validImagesList.push(image);
      }
      setValidImages(validImagesList);
    };
    
    validateImages();
  }, [product]);

  useEffect(() => {
    if (!lightboxImage) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setLightboxImage(null);
        setLightboxCaption('');
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [lightboxImage]);

  const saleCountdownMeta = useMemo(() => {
    if (!product) return { endsAt: null, onSale: false };
    if (product.variants?.length > 0 && selectedVariant !== null) {
      const vid = product.variants[selectedVariant]?.id;
      const vsp = product.variantSalePrices?.[vid];
      if (vsp?.saleInfo) {
        return {
          endsAt: vsp.saleInfo.endsAt || null,
          onSale: true,
        };
      }
    }
    if (product.isOnSale && product.saleInfo) {
      return {
        endsAt: product.saleInfo.endsAt || null,
        onSale: true,
      };
    }
    return { endsAt: null, onSale: false };
  }, [product, selectedVariant]);

  useEffect(() => {
    if (!saleCountdownMeta.endsAt || !saleCountdownMeta.onSale) return;
    const id = setInterval(() => setSaleClockTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [saleCountdownMeta.endsAt, saleCountdownMeta.onSale]);

  const saleEndDate = useMemo(
    () => (saleCountdownMeta.endsAt ? new Date(saleCountdownMeta.endsAt) : null),
    [saleCountdownMeta.endsAt]
  );
  const saleRemainingMs =
    saleEndDate && !Number.isNaN(saleEndDate.getTime())
      ? saleEndDate.getTime() - Date.now()
      : 0;
  const saleCountdownParts = formatCountdownParts(saleRemainingMs);
  const showUrgentSaleCountdown =
    displayPrice.onSale &&
    saleCountdownMeta.onSale &&
    saleRemainingMs > 0 &&
    saleRemainingMs <= THREE_DAYS_MS;

  const mergedCatalogUrls = useMemo(() => {
    if (!product) return [];
    let urls = mergePrimaryIntoImageList(product.primaryImage, product.images || []);
    if (urls.length === 0 && validImages.length > 0) {
      urls = validImages.map((v) => v.localPath).filter(Boolean);
    }
    if (urls.length === 0) {
      const fallback =
        product.primaryImage ||
        (product.images && product.images[0]) ||
        product.customImage ||
        product.printfulData?.designThumbnailUrl;
      if (fallback) urls = [fallback];
    }
    return urls;
  }, [product, validImages]);

  const catalogSlides = useMemo(
    () =>
      mergedCatalogUrls.map((url, i) => ({
        url,
        alt: `${product?.name || 'Product'} — ${i + 1}`,
      })),
    [mergedCatalogUrls, product?.name]
  );

  const handleSubscriptionCheckout = async () => {
    if (!user) {
      showToast('Please create a profile and log in to subscribe.', 'error');
      navigate('/auth?redirect=/product/mystery-test-subscription');
      return;
    }

    if (!user.email) {
      showToast('Please complete your profile with an email address to subscribe.', 'error');
      navigate('/profile');
      return;
    }

    setSubscriptionLoading(true);
    try {
      const isProduction = typeof window !== 'undefined' && 
        (window.location.hostname.includes('rainbow-sails.shop') ||
         (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'));
      const subscriptionApiUrl = process.env.REACT_APP_API_URL || 
        (isProduction ? 'https://api.rainbow-sails.shop/api/v1' : 'http://localhost:4000/api/v1');
      const baseUrl = subscriptionApiUrl.replace('/api/v1', '');
      const response = await fetch(`${baseUrl}/api/subscriptions/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.id,
          customerEmail: user.email,
          customerName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
        }),
      });

      const data = await response.json();

      if (data.success && data.url) {
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        showToast(data.error || 'Failed to create subscription checkout. Please try again.', 'error');
        setSubscriptionLoading(false);
      }
    } catch (error) {
      console.error('❌ Subscription checkout error:', error);
      showToast('An error occurred. Please try again.', 'error');
      setSubscriptionLoading(false);
    }
  };

  const handleAddToCart = () => {
    // Handle subscription separately
    if (product && (product.subscription || product.type === 'subscription')) {
      handleSubscriptionCheckout();
      return;
    }

    // Check if product has variants with length > 0 AND a variant is selected
    if (product && product.variants && product.variants.length > 0 && product.variants[selectedVariant]) {
      const variant = product.variants[selectedVariant];
      logger.logButtonClick('Add to Cart', { 
        productId: product.id, 
        productName: product.name,
        variant: variant.name || variant.size,
        price: variant.price
      });
      logger.logCartAction('add_item', product.id, product.name, {
        variant: variant.name || variant.size,
        price: variant.price
      });
      // Get the correct image URL - use custom image for cart display
      const imageUrl = product.folder 
        ? `/products/${product.folder}/custom-0.jpg`
        : (product.customImage || PLACEHOLDER_IMAGE);
      
      const cartItem = {
        id: `${product.id}-${variant.variantId}`, // Use Printful variant ID
        title: `${product.name} / ${variant.size}`,  // ← Make title more descriptive
        image: imageUrl,  // ← Use the correct image from galleryImages
        price: getEffectivePriceForCart(variant),
        variant: variant.color || variant.size,
        size: variant.size,
        type: 'product',
        // Store correct IDs for order processing
        productId: product.id,
        variantId: variant.variantId,
        printfulVariantId: variant.printfulVariantId,
        printfulProductId: product.printfulData?.printfulProductId,
        // Add Printful order data for checkout
        printful_data: {
          variant_id: variant.printfulVariantId,
          files: [{
            type: "front",
            url: variant.order_data?.files?.[0]?.url || product.variants?.[0]?.order_data?.files?.[0]?.url || "No image available"
          }]
        }
      };
      for (let q = 0; q < quantity; q++) addToCart(cartItem);
      showToast(`Added to cart${quantity > 1 ? ` (×${quantity})` : ''}!`, 'success');
    } else if (product && (!product.variants || product.variants.length === 0)) {
      // Product has no variants or empty variants array - add the basic product
      logger.logButtonClick('Add to Cart', { 
        productId: product.id, 
        productName: product.name,
        price: product.price
      });
      logger.logCartAction('add_item', product.id, product.name, {
        price: product.price
      });
      
      // Get product image - check multiple sources
      const imageUrl = product.primaryImage || 
                       product.customImage || 
                       (product.images && product.images.length > 0 ? product.images[0] : null) ||
                       (product.folder ? `/products/${product.folder}/custom-0.jpg` : PLACEHOLDER_IMAGE);
      
      const cartItem = {
        id: product.id,
        title: product.name,
        image: imageUrl,
        price: getEffectivePriceForCart(null),
        variant: 'default',
        size: 'default',
        type: 'product',
        // Store correct IDs for order processing
        productId: product.id,
        variantId: product.id,
        printfulVariantId: null,
        printfulProductId: product.printfulData?.printfulProductId
      };
      for (let q = 0; q < quantity; q++) addToCart(cartItem);
      showToast(`Added to cart${quantity > 1 ? ` (×${quantity})` : ''}!`, 'success');
    } else {
      showToast('Please select a size or style before adding to cart', 'error');
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
        paddingTop: '80px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px', alignItems: 'start' }}>
            <div>
              <div style={{ width: '100%', aspectRatio: '1', background: 'rgba(255,255,255,0.08)', borderRadius: '16px', animation: 'productSkeletonPulse 1.5s ease-in-out infinite' }} />
            </div>
            <div>
              <div style={{ height: 48, background: 'rgba(255,255,255,0.08)', borderRadius: '8px', marginBottom: 16, width: '80%', animation: 'productSkeletonPulse 1.5s ease-in-out infinite' }} />
              <div style={{ height: 24, background: 'rgba(255,255,255,0.08)', borderRadius: '8px', marginBottom: 24, width: '50%', animation: 'productSkeletonPulse 1.5s ease-in-out infinite' }} />
              <div style={{ height: 96, background: 'rgba(255,255,255,0.08)', borderRadius: '12px', marginBottom: 24, animation: 'productSkeletonPulse 1.5s ease-in-out infinite' }} />
              <div style={{ height: 56, background: 'rgba(255,255,255,0.08)', borderRadius: '12px', width: '100%', animation: 'productSkeletonPulse 1.5s ease-in-out infinite' }} />
            </div>
          </div>
        </div>
        <style>{`@keyframes productSkeletonPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
      </div>
    );
  }

  if (!product) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: '80px'
      }}>
        <div style={{ textAlign: 'center', color: 'white' }}>
          <p style={{ fontSize: '18px', marginBottom: '16px' }}>Product not found</p>
          <button
            onClick={() => navigate('/gallery')}
            style={{
              background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Back to Gallery
          </button>
        </div>
      </div>
    );
  }

  const currentVariant = product?.variants?.[selectedVariant] || null;

  const mainImageSrc = (() => {
    // Priority: primaryImage > images[0] > customImage > variant image > Printful > placeholder
    if (selectedVariant !== null && product.variants && product.variants[selectedVariant]) {
      const variant = product.variants[selectedVariant];
      // Check variant-specific image first
      if (variant.image) return variant.image;
      // Then check product folder (legacy support)
      if (product.folder) {
        return `/products/${product.folder}/${variant.previewImage || variant.defaultImage}`;
      }
    }
    
    // Use primaryImage (already full URL from productsService)
    if (product.primaryImage) return product.primaryImage;
    
    // Use first image from images array (already full URLs from productsService)
    if (product.images && product.images.length > 0) return product.images[0];
    
    // Use customImage (already full URL from productsService)
    if (product.customImage) return product.customImage;
    
    // Legacy folder support
    if (product.folder) {
      return `/products/${product.folder}/custom-0.jpg`;
    }
    
    // Printful fallback
    if (product.printfulData?.designThumbnailUrl) {
      return product.printfulData.designThumbnailUrl;
    }
    
    // Final fallback to placeholder constant
    return PLACEHOLDER_IMAGE;
  })();

  const topSliderSlides = catalogSlides.slice(0, 3);
  /** Images after highlights — no overlap with first 3 (max 13) */
  const middleSliderSlides = catalogSlides.slice(3, 3 + MAX_FULL_GALLERY_SLIDES);
  const extraCatalogCount = Math.max(0, mergedCatalogUrls.length - 3 - MAX_FULL_GALLERY_SLIDES);

  /** Only shortDescription here — avoids repeating the opening of `description` in Details */
  const artIntroText = product?.shortDescription?.trim() || null;
  const publishedReviewCount = getProductReviews(product.id).length;

  const storeUrl = process.env.REACT_APP_STORE_URL || (typeof window !== 'undefined' ? window.location.origin : 'https://rainbow-sails.shop');
  const productSlug = product.slug || product.name?.toLowerCase().replace(/\s+/g, '-') || slug;
  const productUrl = `${storeUrl}/product/${productSlug}`;
  const communityHref = `/product/${productSlug}/community`;
  const ogImage = mainImageSrc?.startsWith('http') ? mainImageSrc : (mainImageSrc ? `${storeUrl}${mainImageSrc.startsWith('/') ? '' : '/'}${mainImageSrc}` : null);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
      paddingTop: '80px'
    }}>
      <Helmet>
        <title>{product.name} | Rainbow Sails</title>
        <meta name="description" content={product.shortDescription || product.description?.slice(0, 160) || product.name} />
        <meta property="og:title" content={`${product.name} | Rainbow Sails`} />
        <meta property="og:description" content={product.shortDescription || product.description?.slice(0, 160) || product.name} />
        <meta property="og:url" content={productUrl} />
        {ogImage && <meta property="og:image" content={ogImage} />}
        <link rel="canonical" href={productUrl} />
      </Helmet>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: window.innerWidth <= 768 ? '32px 16px' : '32px 24px',
        width: '100%',
        boxSizing: 'border-box',
        position: 'relative',
        zIndex: 10
      }}>
        {/* Back Button - use history when available */}
        <button
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/gallery'))}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: 'white',
            padding: '12px 16px',
            borderRadius: '8px',
            cursor: 'pointer',
            marginBottom: '32px',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.1)';
          }}
        >
          <ArrowLeft size={20} />
          Back to Gallery
        </button>

        <div style={{
          maxWidth: 720,
          margin: '0 auto',
          width: '100%',
        }}>
          {(product.subscription || product.type === 'subscription') ? (
            <div style={{ marginBottom: 28 }}>
                <div style={{
                  width: '100%',
                  minHeight: '400px',
                  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.4), rgba(236, 72, 153, 0.4))',
                  borderRadius: '16px',
                  boxShadow: '0 20px 40px rgba(139, 92, 246, 0.5)',
                  border: '2px solid rgba(139, 92, 246, 0.6)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <img
                    src="/favicon.png"
                    alt="Rainbow Sails Subscription"
                    style={{
                      width: '200px',
                      height: '200px',
                      objectFit: 'contain',
                      animation: 'pulse 2s ease-in-out infinite',
                      filter: 'drop-shadow(0 0 30px rgba(255, 255, 255, 0.6))',
                      marginBottom: '20px'
                    }}
                    onError={(e) => {
                      e.target.src = '/Logo.png';
                    }}
                  />
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '12px',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: '2px'
                  }}>
                    Zee Mystery Subscription Test
                  </div>
                  <div style={{
                    position: 'absolute',
                    bottom: '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    color: '#e9d5ff',
                    fontSize: '14px',
                    fontStyle: 'italic',
                    textAlign: 'center',
                    padding: '0 20px'
                  }}>
                    The secret stays sealed until Zee drops the next test surprise!
                  </div>
                </div>
            </div>
              ) : (
                topSliderSlides.length > 0 ? (
                  <ProductImageSlider
                    images={topSliderSlides}
                    title="Highlights"
                    productName={product.name}
                    onImageClick={(url, alt) => openLightbox(url, `${alt} • ${product.name}`)}
                    hideIfEmpty
                  />
                ) : null
              )}

          <div style={{ marginBottom: '24px' }}>
              <h1 style={{
                fontSize: 'clamp(1.75rem, 4vw, 2.35rem)',
                fontWeight: 700,
                marginBottom: 12,
                textAlign: 'left',
                color: '#f9fafb',
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
              }}>
                {product.name}
              </h1>
              <p style={{
                color: '#6ee7b7',
                fontSize: 13,
                fontWeight: 500,
                marginTop: 0,
                marginBottom: 16,
              }}>
                {selectedVariant !== null && currentVariant?.size
                  ? currentVariant.size
                  : product.variants?.length > 0
                    ? 'Choose a style below'
                    : (product.size_info?.material || '')}
                {product.size_info?.material ? ` • ${product.size_info.material}` : ''}
              </p>
              
              {(product.artist || product.brand) && (
                <p style={{
                  color: '#9ca3af',
                  fontSize: '18px',
                  marginBottom: '8px'
                }}>
                  {product.artist && product.brand 
                    ? `by ${product.artist} • ${product.brand}`
                    : product.artist 
                      ? `by ${product.artist}`
                      : product.brand
                        ? product.brand
                        : ''}
                </p>
              )}
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                {(product.category || product.technique) && (
                <div style={{
                  background: 'rgba(59, 130, 246, 0.1)',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  display: 'inline-block'
                }}>
                  <span style={{ color: '#3b82f6', fontSize: '14px', fontWeight: '500' }}>
                    {[product.category, product.technique].filter(Boolean).join(' • ')}
                  </span>
                </div>
                )}
                <button
                  onClick={handleShare}
                  disabled={sharing}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    padding: '10px 16px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    cursor: sharing ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  title="Share with friends"
                  aria-label="Share product with friends"
                >
                  <Share2 size={16} />
                  {sharing ? 'Sharing...' : 'Share'}
                </button>
              </div>
            </div>

            {artIntroText && !(product.subscription || product.type === 'subscription') && (
              <section style={{ marginBottom: 28 }}>
                <h2 style={{
                  color: '#9ca3af',
                  fontSize: '12px',
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  margin: '0 0 10px',
                }}>
                  About this piece
                </h2>
                <p style={{
                  color: '#e5e7eb',
                  fontSize: '16px',
                  lineHeight: 1.65,
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                }}>
                  {artIntroText}
                </p>
              </section>
            )}

            {(product.subscription || product.type === 'subscription') && (
              <div style={{
                marginBottom: '24px',
                background: 'rgba(59, 130, 246, 0.12)',
                border: '1px solid rgba(59, 130, 246, 0.35)',
                borderRadius: '10px',
                padding: '16px'
              }}>
                <p style={{
                  color: '#bfdbfe',
                  fontSize: '16px',
                  lineHeight: '1.6',
                  margin: '0 0 8px 0'
                }}>
                  ℹ️ Zee\'s reminder: auto-renew starts off. After checkout, open Profile &gt; Subscription and enable auto-renew if you want to stay in the $1 mystery subscription test.
                </p>
                <p style={{
                  color: '#cbd5f5',
                  fontSize: '15px',
                  lineHeight: '1.6',
                  margin: 0
                }}>
                  Testers who stick with Zee will be first to receive the mystery drop when the experiment wraps.
                </p>
              </div>
            )}

            {/* Variant Selection */}
            {product.variants && product.variants.length > 1 && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  marginBottom: '12px',
                  color: 'white'
                }}>
                  Choose Your{' '}
                  <span style={{
                    background: 'linear-gradient(45deg, #ec4899, #8b5cf6, #06b6d4, #10b981, #f59e0b, #ec4899)',
                    backgroundSize: '300% 300%',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                    animation: 'gradientShift 3s ease-in-out infinite'
                  }}>
                    Style
                  </span>:
                </h3>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {product.variants.map((variant, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedVariant(index)}
                      style={{
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: selectedVariant === index 
                          ? '2px solid #ec4899' 
                          : '1px solid rgba(255, 255, 255, 0.2)',
                        background: selectedVariant === index 
                          ? 'rgba(236, 72, 153, 0.2)' 
                          : 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        minWidth: '120px'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedVariant !== index) {
                          e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                          e.target.style.borderColor = 'rgba(236, 72, 153, 0.5)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedVariant !== index) {
                          e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                          e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                        }
                      }}
                    >
                      {(() => {
                        // Extract text after "/" from variant.name, fallback to size/name/color
                        if (variant.name && variant.name.includes('/')) {
                          return variant.name.split('/')[1]?.trim() || variant.size || variant.name || variant.color;
                        }
                        return variant.size || variant.name || variant.color;
                      })()}
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                        {product.variantSalePrices?.[variant.id]?.saleInfo
                          ? <><span style={{ color: '#10b981' }}>{formatPrice(product.variantSalePrices[variant.id].salePrice)}</span> <span style={{ textDecoration: 'line-through', marginLeft: 4 }}>{formatPrice(product.variantSalePrices[variant.id].originalPrice)}</span></>
                          : formatPrice(variant.price)
                        }
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Price & Add to Cart - sticky on mobile */}
            <div
              className="product-add-to-cart-section"
              style={{
                background: 'rgba(0, 0, 0, 0.6)',
                padding: '24px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              <div style={{ marginBottom: '20px' }}>
                {(displayPrice.onSale && saleInfo?.label) && (
                  <span style={{
                    display: 'inline-block',
                    padding: '4px 10px',
                    background: 'rgba(16, 185, 129, 0.2)',
                    color: '#10b981',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600',
                    marginBottom: '8px'
                  }}>
                    {saleInfo.label}
                  </span>
                )}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                  <p style={{
                    color: (product.subscription || product.type === 'subscription') ? '#c4b5fd' : '#10b981',
                    fontSize: '32px',
                    fontWeight: 'bold',
                    margin: 0
                  }}>
                    {displayPrice.priceRange
                      ? `${formatPrice(displayPrice.priceRange[0])} - ${formatPrice(displayPrice.priceRange[1])}`
                      : formatPrice(displayPrice.price)
                    }
                  </p>
                  {displayPrice.onSale && displayPrice.original != null && (
                    <span style={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: '18px' }}>
                      {formatPrice(displayPrice.original)}
                    </span>
                  )}
                  {(() => {
                    // For service products (coaching), use remainingSpots (calculated from maxClients - purchases)
                    // For physical products, use inventoryQty
                    const spotsAvailable = product.productType === 'service' || product.type === 'service'
                      ? (product.remainingSpots !== undefined && product.remainingSpots !== null ? product.remainingSpots : product.maxClients)
                      : product.inventoryQty;
                    
                    // Only show if there are spots available (greater than 0)
                    if (spotsAvailable !== undefined && spotsAvailable !== null && spotsAvailable > 0) {
                      return (
                        <span style={{
                          color: '#60a5fa',
                          fontSize: '18px',
                          fontWeight: '500',
                          marginLeft: '12px'
                        }}>
                          {spotsAvailable === 1 ? '1 spot left' : `${spotsAvailable} spots available`}
                        </span>
                      );
                    }
                    return null;
                  })()}
                  {(product.subscription || product.type === 'subscription') && (
                    <span style={{
                      color: '#c4b5fd',
                      fontSize: '18px',
                      fontWeight: '500'
                    }}>
                      /month
                    </span>
                  )}
                </div>
                <p style={{
                  color: '#9ca3af',
                  fontSize: '14px',
                  margin: '4px 0 0 0'
                }}>
                  {(product.subscription || product.type === 'subscription') 
                    ? 'Zee Mystery Subscription Test • billed monthly during the experiment'
                    : `${currentVariant?.size || ''} ${currentVariant?.size ? '•' : ''} ${product.size_info?.material || 'Premium Quality'}`}
                </p>
                {displayPrice.onSale && saleCountdownMeta.onSale && saleEndDate && saleRemainingMs > 0 && (
                  <p style={{ color: '#fcd34d', fontSize: '13px', margin: '10px 0 0', fontWeight: 500 }}>
                    Sale ends {saleEndDate.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                )}
                {showUrgentSaleCountdown && saleCountdownParts && (
                  <div
                    role="timer"
                    aria-live="polite"
                    aria-atomic="true"
                    style={{
                      marginTop: 12,
                      padding: '12px 14px',
                      borderRadius: 10,
                      background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(239, 68, 68, 0.15))',
                      border: '1px solid rgba(251, 191, 36, 0.45)',
                    }}
                  >
                    <p style={{ color: '#fef3c7', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 6px' }}>
                      Final 3 days — price ends soon
                    </p>
                    <p style={{ color: '#fff', fontSize: '20px', fontWeight: 800, fontVariantNumeric: 'tabular-nums', margin: 0 }}>
                      {saleCountdownParts.d > 0 ? `${saleCountdownParts.d}d ` : ''}
                      {String(saleCountdownParts.h).padStart(2, '0')}:
                      {String(saleCountdownParts.m).padStart(2, '0')}:
                      {String(saleCountdownParts.sec).padStart(2, '0')}
                    </p>
                  </div>
                )}
              </div>

              {(product.subscription || product.type === 'subscription') && !user && (
                <div style={{
                  background: 'rgba(236, 72, 153, 0.1)',
                  border: '1px solid rgba(236, 72, 153, 0.3)',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  <p style={{
                    color: '#fbcfe8',
                    fontSize: '14px',
                    margin: 0
                  }}>
                    ⚠️ You must have a profile to subscribe. Please <a href="/auth" style={{ color: '#ec4899', textDecoration: 'underline' }}>sign in or create your Rainbow Sails account</a> first.
                  </p>
                </div>
              )}

              {/* Quantity selector for non-subscription products */}
              {!(product.subscription || product.type === 'subscription') && (
                <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>Quantity:</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '8px', padding: '4px' }}>
                    <button
                      type="button"
                      aria-label="Decrease quantity"
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '6px',
                        border: '1px solid rgba(255,255,255,0.2)',
                        background: 'rgba(255,255,255,0.1)',
                        color: 'white',
                        fontSize: '18px',
                        cursor: 'pointer'
                      }}
                    >
                      −
                    </button>
                    <span style={{ color: 'white', minWidth: '28px', textAlign: 'center', fontSize: '16px', fontWeight: '600' }}>{quantity}</span>
                    <button
                      type="button"
                      aria-label="Increase quantity"
                      onClick={() => setQuantity((q) => Math.min(99, q + 1))}
                      style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '6px',
                        border: '1px solid rgba(255,255,255,0.2)',
                        background: 'rgba(255,255,255,0.1)',
                        color: 'white',
                        fontSize: '18px',
                        cursor: 'pointer'
                      }}
                    >
                      +
                    </button>
                  </div>
                </div>
              )}

              <button
                onClick={handleAddToCart}
                disabled={subscriptionLoading || ((product.subscription || product.type === 'subscription') && !user)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  padding: '16px',
                  background: (product.subscription || product.type === 'subscription') 
                    ? 'linear-gradient(135deg, #8b5cf6, #ec4899)' 
                    : 'linear-gradient(135deg, #10b981, #059669)',
                  color: 'white',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: (subscriptionLoading || ((product.subscription || product.type === 'subscription') && !user)) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: (product.subscription || product.type === 'subscription')
                    ? '0 4px 15px rgba(139, 92, 246, 0.5)'
                    : '0 4px 15px rgba(16, 185, 129, 0.3)',
                  opacity: (subscriptionLoading || ((product.subscription || product.type === 'subscription') && !user)) ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!subscriptionLoading && !((product.subscription || product.type === 'subscription') && !user)) {
                    e.target.style.background = (product.subscription || product.type === 'subscription')
                      ? 'linear-gradient(135deg, #7c3aed, #db2777)'
                      : 'linear-gradient(135deg, #059669, #047857)';
                    e.target.style.transform = 'scale(1.02)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!subscriptionLoading && !((product.subscription || product.type === 'subscription') && !user)) {
                    e.target.style.background = (product.subscription || product.type === 'subscription')
                      ? 'linear-gradient(135deg, #8b5cf6, #ec4899)'
                      : 'linear-gradient(135deg, #10b981, #059669)';
                    e.target.style.transform = 'scale(1)';
                  }
                }}
              >
                <ShoppingBag size={20} />
                {subscriptionLoading 
                  ? 'Processing...' 
                  : (product.subscription || product.type === 'subscription') 
                    ? (!user ? 'Sign In to Subscribe' : 'Subscribe Now') 
                    : 'Add to Cart'}
              </button>
              
              {(product.subscription || product.type === 'subscription') && user && (
                <div style={{
                  background: 'rgba(139, 92, 246, 0.1)',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '8px',
                  padding: '12px',
                  marginTop: '12px',
                  fontSize: '12px',
                  color: '#c4b5fd',
                  textAlign: 'center'
                }}>
                  <p style={{ margin: 0 }}>
                    ⚠️ Zee\'s reminder: auto-renew starts OFF. After subscribing, open Profile &gt; Subscription and tap "Enable Auto-Renew" to stay in the $1 mystery subscription test and claim the final drop.
                  </p>
                </div>
              )}
            </div>

            {!(product.subscription || product.type === 'subscription') && middleSliderSlides.length > 0 && (
              <ProductImageSlider
                images={middleSliderSlides}
                title={
                  extraCatalogCount > 0
                    ? `More photos (${middleSliderSlides.length} shown, ${extraCatalogCount} more in catalog)`
                    : 'More photos'
                }
                productName={product.name}
                onImageClick={(url, alt) => openLightbox(url, `${alt} • ${product.name}`)}
                hideIfEmpty
              />
            )}

            {!(product.subscription || product.type === 'subscription') && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                marginBottom: 28,
              }}>
                <Link
                  to={communityHref}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                    padding: '14px 18px',
                    borderRadius: 12,
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.18)',
                    color: '#f9fafb',
                    fontWeight: 600,
                    fontSize: 15,
                    textDecoration: 'none',
                  }}
                >
                  <Users size={18} aria-hidden />
                  Community photos &amp; social posts
                </Link>
                <p style={{ color: '#9ca3af', fontSize: 13, lineHeight: 1.55, margin: 0 }}>
                  Got this piece? Post a photo of you with it on Instagram or Threads, tag us if you like, and add{' '}
                  <strong style={{ color: '#e5e7eb' }}>#RainbowSails</strong>
                  {productSlug ? (
                    <>
                      {' '}
                      plus <strong style={{ color: '#e5e7eb' }}>#{productSlug.replace(/-/g, '')}</strong>
                    </>
                  ) : null}{' '}
                  so we can feature you. The community page has the full how-to.
                </p>
                <Link
                  to="/social"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                    padding: '12px 18px',
                    borderRadius: 12,
                    color: '#93c5fd',
                    fontWeight: 500,
                    fontSize: 14,
                    textDecoration: 'none',
                  }}
                >
                  Browse the Zee Social stream
                </Link>
              </div>
            )}

            {product.description && product.description.trim() !== (artIntroText || '').trim() && (
              <div style={{ marginBottom: 32 }}>
                <h3 style={{
                  color: '#f3f4f6',
                  fontSize: '15px',
                  fontWeight: 700,
                  marginBottom: 12,
                  letterSpacing: '0.02em',
                }}>
                  Details
                </h3>
                <div style={{
                  color: '#d1d5db',
                  fontSize: '16px',
                  lineHeight: 1.65,
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                }}>
                  {product.description}
                </div>
              </div>
            )}

            {/* Product Specifications */}
            {product && (
              <div style={{
                marginTop: '32px',
                background: 'rgba(59, 130, 246, 0.1)',
                padding: '20px',
                borderRadius: '12px',
                border: '1px solid rgba(59, 130, 246, 0.2)'
              }}>
                <h4 style={{
                  color: '#3b82f6',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  marginBottom: '16px'
                }}>
                  Product Specifications
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '16px'
                }}>
                  {/* Extract real data from Printful API response */}
                  {product.category && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px', display: 'block' }}>Category</span>
                      <span style={{ color: 'white', fontSize: '16px', fontWeight: '500' }}>
                        {product.category}
                      </span>
                    </div>
                  )}
                  {product.technique && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px', display: 'block' }}>Technique</span>
                      <span style={{ color: 'white', fontSize: '16px', fontWeight: '500' }}>
                        {product.technique}
                      </span>
                    </div>
                  )}
                  {currentVariant?.size && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px', display: 'block' }}>Size</span>
                      <span style={{ color: 'white', fontSize: '16px', fontWeight: '500' }}>
                        {currentVariant.size}
                      </span>
                    </div>
                  )}
                  {currentVariant?.color && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px', display: 'block' }}>Color</span>
                      <span style={{ color: 'white', fontSize: '16px', fontWeight: '500' }}>
                        {currentVariant.color}
                      </span>
                    </div>
                  )}
                  {/* Extract capacity from description */}
                  {product.description && product.description.match(/\d+\s*(oz|ml|fl oz)/i) && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px', display: 'block' }}>Capacity</span>
                      <span style={{ color: 'white', fontSize: '16px', fontWeight: '500' }}>
                        {product.description.match(/\d+\s*(oz|ml|fl oz)/i)?.[0]}
                      </span>
                    </div>
                  )}
                  {/* Extract dimensions from description */}
                  {product.description && product.description.match(/Dimensions:?\s*[^•\n]+/i) && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px', display: 'block' }}>Dimensions</span>
                      <span style={{ color: 'white', fontSize: '16px', fontWeight: '500' }}>
                        {product.description.match(/Dimensions:?\s*([^•\n]+)/i)?.[1]?.trim()}
                      </span>
                    </div>
                  )}
                  {/* Extract material from description */}
                  {product.description && product.description.match(/(stainless steel|cotton|polyester|metal|plastic|wood|leather|fabric)/i) && (
                    <div>
                      <span style={{ color: '#9ca3af', fontSize: '14px', display: 'block' }}>Material</span>
                      <span style={{ color: 'white', fontSize: '16px', fontWeight: '500' }}>
                        {product.description.match(/(stainless steel|high-grade stainless steel|cotton|polyester|metal|plastic|wood|leather|fabric)/i)?.[0]}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
        </div>

        {/* You might also like */}
        {relatedProducts.length > 0 && (
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
            <h2 style={{ color: 'white', fontSize: '24px', fontWeight: 'bold', marginBottom: '24px', textAlign: 'center' }}>
              You might also like
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '20px' }}>
              {relatedProducts.map((p) => {
                const img = p.primaryImage || p.customImage || (p.images?.[0]) || PLACEHOLDER_IMAGE;
                const prodSlug = p.slug || p.name?.toLowerCase().replace(/\s+/g, '-') || p.id;
                const href = `/product/${prodSlug}`;
                return (
                  <div
                    key={p.id}
                    onClick={() => navigate(href)}
                    style={{
                      background: 'rgba(0,0,0,0.4)',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: '1px solid rgba(255,255,255,0.1)',
                      transition: 'transform 0.2s, box-shadow 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.02)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ aspectRatio: '1', overflow: 'hidden' }}>
                      <img src={img} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" onError={(ev) => { ev.target.src = PLACEHOLDER_IMAGE; }} />
                    </div>
                    <div style={{ padding: '12px' }}>
                      <p style={{ color: 'white', fontSize: '14px', fontWeight: '600', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</p>
                      <p style={{ color: '#10b981', fontSize: '16px', fontWeight: 'bold', margin: '4px 0 0 0' }}>
                        {p.isOnSale && p.salePrice != null ? formatPrice(p.salePrice) : formatPrice(p.price || 0)}
                        {p.isOnSale && p.originalPrice != null && <span style={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: '12px', marginLeft: 6 }}>{formatPrice(p.originalPrice)}</span>}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {artistMoreProducts.length > 0 && product?.artist?.trim() && (
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
            <h2 style={{ color: 'white', fontSize: '24px', fontWeight: 'bold', marginBottom: '24px', textAlign: 'center' }}>
              More from {product.artist.trim()}
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '20px' }}>
              {artistMoreProducts.map((p) => {
                const img = p.primaryImage || p.customImage || (p.images?.[0]) || PLACEHOLDER_IMAGE;
                const prodSlug = p.slug || p.name?.toLowerCase().replace(/\s+/g, '-') || p.id;
                const href = `/product/${prodSlug}`;
                return (
                  <div
                    key={p.id}
                    onClick={() => navigate(href)}
                    style={{
                      background: 'rgba(0,0,0,0.4)',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: '1px solid rgba(255,255,255,0.1)',
                      transition: 'transform 0.2s, box-shadow 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.02)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ aspectRatio: '1', overflow: 'hidden' }}>
                      <img src={img} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" onError={(ev) => { ev.target.src = PLACEHOLDER_IMAGE; }} />
                    </div>
                    <div style={{ padding: '12px' }}>
                      <p style={{ color: 'white', fontSize: '14px', fontWeight: '600', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</p>
                      <p style={{ color: '#10b981', fontSize: '16px', fontWeight: 'bold', margin: '4px 0 0 0' }}>
                        {p.isOnSale && p.salePrice != null ? formatPrice(p.salePrice) : formatPrice(p.price || 0)}
                        {p.isOnSale && p.originalPrice != null && <span style={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: '12px', marginLeft: 6 }}>{formatPrice(p.originalPrice)}</span>}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Collector reviews — only when at least one approved note exists */}
        {publishedReviewCount > 0 && (
          <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
            <ProductReviews productId={product.id} productName={product.name} />
          </div>
        )}
      </div>

      {lightboxImage && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Zee full screen image viewer"
          onClick={closeLightbox}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '24px',
            zIndex: 1000,
            backdropFilter: 'blur(6px)'
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              maxWidth: '95vw',
              maxHeight: '85vh',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px'
            }}
          >
            <button
              onClick={closeLightbox}
              style={{
                alignSelf: 'flex-end',
                background: 'rgba(255, 255, 255, 0.12)',
                color: '#f472b6',
                border: '1px solid rgba(244, 114, 182, 0.4)',
                borderRadius: '9999px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.background = 'rgba(244, 114, 182, 0.25)';
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
              }}
            >
              Zee Close
            </button>
            <img
              src={lightboxImage}
              alt={lightboxCaption || 'Zee product close up'}
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: '16px',
                boxShadow: '0 30px 60px rgba(0, 0, 0, 0.6)',
                cursor: 'zoom-out'
              }}
            />
            {lightboxCaption && (
              <p style={{ color: '#e5e7eb', fontSize: '16px', textAlign: 'center' }}>
                {lightboxCaption}
              </p>
            )}
            <p style={{ color: '#9ca3af', fontSize: '14px', textAlign: 'center' }}>
              Zee tip: pinch, spread, or scroll to zoom; tap outside to close.
            </p>
          </div>
        </div>
      )}
      
      {/* CSS for gradient animation, trippy effects, and sticky Add to Cart on mobile */}
      <style>{`
        @media (max-width: 768px) {
          .product-add-to-cart-section {
            position: sticky;
            bottom: 0;
            z-index: 50;
            box-shadow: 0 -4px 20px rgba(0,0,0,0.3);
          }
        }
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        @keyframes rainbowDrip {
          0% { 
            background-position: 0% 50%;
            filter: drop-shadow(0 0 20px rgba(255, 0, 128, 0.5)) hue-rotate(0deg);
          }
          25% { 
            background-position: 100% 50%;
            filter: drop-shadow(0 0 25px rgba(255, 128, 0, 0.7)) hue-rotate(90deg);
          }
          50% { 
            background-position: 100% 50%;
            filter: drop-shadow(0 0 30px rgba(128, 255, 0, 0.8)) hue-rotate(180deg);
          }
          75% { 
            background-position: 0% 50%;
            filter: drop-shadow(0 0 25px rgba(0, 255, 255, 0.7)) hue-rotate(270deg);
          }
          100% { 
            background-position: 0% 50%;
            filter: drop-shadow(0 0 20px rgba(255, 0, 128, 0.5)) hue-rotate(360deg);
          }
        }
        
        @keyframes textGlow {
          0% { 
            text-shadow: 0 0 30px rgba(255, 0, 128, 0.8), 0 0 60px rgba(255, 128, 0, 0.6), 0 0 90px rgba(255, 255, 0, 0.4);
            transform: perspective(500px) rotateX(15deg) scale(1);
          }
          100% { 
            text-shadow: 0 0 40px rgba(255, 0, 128, 1), 0 0 80px rgba(255, 128, 0, 0.8), 0 0 120px rgba(255, 255, 0, 0.6), 0 0 160px rgba(0, 255, 255, 0.4);
            transform: perspective(500px) rotateX(15deg) scale(1.02);
          }
        }
        
        @keyframes pulse {
          0%, 100% { 
            transform: scale(1);
            opacity: 1;
          }
          50% { 
            transform: scale(1.1);
            opacity: 0.8;
          }
        }
      `}</style>
    </div>
  );
};

export default Product;